import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Link,
  Stack,
  Badge,
  Card,
  SvgIcon,
  Tooltip,
  CircularProgress,
  Paper,
  Theme,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import useAfasData from "../hooks/useAfasData";
import { useContext, useEffect, useState } from "react";
import { Gladior_Management_Employees, Gladior_Management_Ommissions } from "../types/types";
import { InSiteContext } from "../context/InSiteContext";
import dayjs from "dayjs";
import { LanguageContext } from "../context/LanguageContext";
import { RoleContext } from "../context/RoleContext";

// Interface for an absent employee

interface OmissionOverview {
  absence: Gladior_Management_Ommissions;
  frequentAbsence: { employee_id: string; name: string; frequency: number }[];
  team: Gladior_Management_Employees;
  summary: {
    frequency: number;
    total: number;
    totalEmployees: number;
    totalFte: number;
  };
}

const OmissionComponent: React.FC = () => {
  const inSiteUrl = useContext(InSiteContext);
  const role = useContext(RoleContext);
  const { strings } = useContext(LanguageContext);
  const [ommission, setOmmission] = useState<OmissionOverview | null>(null);
  const { data: ommissionData, isLoading: ommissionIsLoading } = useAfasData("/ommission/overview");
  useEffect(() => {
    if (!ommissionIsLoading && ommissionData) {
      setOmmission(ommissionData);
    }
  }, [ommissionData]);

  return (
    <Paper
      elevation={0}
      sx={(theme: Theme) => ({
        height: "100%",
        bgcolor: theme.palette.light.main,
        py: 4,
        pl: 3,
        pr: 3,
      })}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header with title and count badge */}
        <Box display="flex" alignItems="flex-start" mb={4} justifyContent={"space-between"}>
          <Badge color="warning" badgeContent={ommission?.absence.length}>
            <Typography variant="h3" component="h3">
              {strings.ommission.title}
            </Typography>
          </Badge>
          <Box textAlign="right">
            <Link
              color="initial"
              href={`${inSiteUrl}/verzuimbegeleiding-medewerkerkeuze-prs/dashboard-managers-openstaand-verzuim`}
              target="_parent"
              underline="hover"
              sx={{ textDecoration: "underline", fontWeight: 400 }}
            >
              {strings.ommission.all} &gt;
            </Link>
          </Box>
        </Box>

        {/* Currently absent employees */}
        <Box sx={{ flex: "1 1 100%" }} mb={4}>
          <Box display="flex" justifyContent="space-between" pb={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              {ommission?.absence.length}{" "}
              {Number(ommission?.absence?.length) !== 1
                ? strings.ommission.colleagues
                : strings.ommission.colleague}{" "}
              {strings.ommission.absent}
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              {strings.ommission.since}
            </Typography>
          </Box>
          <Divider sx={{ my: ".25rem" }} />
          {ommissionIsLoading && !ommission && <CircularProgress />}
          {ommission?.absence && ommission?.absence?.length > 0 && (
            <List disablePadding sx={{ mb: 3 }}>
              {ommission?.absence.map((employee) => (
                <ListItem key={employee.id} sx={{ py: ".25rem", px: 0 }}>
                  <ListItemText
                    primary={
                      <Tooltip
                        arrow
                        placement="top"
                        title={ommission?.team.find((member) => member.employee === employee.employee)?.name}
                      >
                        <Typography
                          sx={{
                            display: "-webkit-box",
                            "-webkit-line-clamp": "1",
                            lineClamp: "1",
                            "-webkit-box-orient": "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          variant="body1"
                          color="initial"
                          component={"span"}
                        >
                          {ommission?.team.find((member) => member.employee === employee.employee)?.name}
                        </Typography>
                      </Tooltip>
                    }
                  />
                  <Typography sx={{ whiteSpace: "pre" }}>
                    {dayjs(employee.start_date).format("D-MM-YYYY")}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {role === "manager" && (
          <>
            {/* Frequently absent employees */}
            {Number(ommission?.frequentAbsence?.length) > 0 && (
              <Box my={2}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {strings.ommission.frequent}
                </Typography>
                <Divider sx={{ my: ".25rem" }} />
                <List disablePadding sx={{ mb: 3 }}>
                  {ommission?.frequentAbsence.map((info) => (
                    <ListItem key={info.employee_id} sx={{ py: ".25rem", px: 0 }}>
                      <ListItemText
                        primary={
                          <>
                            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                              {info.name}
                              <Tooltip
                                arrow
                                placement="top"
                                title={strings.formatString(
                                  strings.ommission.based,
                                  `${ommission?.team
                                    .find((member) => member.employee === info.employee_id)
                                    ?.fte.toFixed(1)}`
                                )}
                              >
                                <Chip size="small" sx={{ ml: 1 }} label={info.frequency.toFixed(1)} />
                              </Tooltip>
                            </Stack>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Statistics section */}
            <Box mt={2}>
              <Typography mb={3} variant="h6">
                {strings.ommission.last12months}
              </Typography>

              <Grid container spacing={1} alignItems={"center"}>
                <Grid size={3}>
                  <Stack direction={"row"} sx={{ alignItems: "center", justifyContent: "center" }}>
                    <Tooltip
                      arrow
                      placement="top"
                      title={strings.formatString(
                        strings.ommission.based,
                        `${ommission?.summary.totalFte.toFixed(1)}`
                      )}
                    >
                      <Card
                        sx={(theme: Theme) => ({
                          bgcolor: theme.palette.light.light,
                          padding: 0,
                          width: 71,
                          height: 56,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                        })}
                      >
                        {ommissionIsLoading && !ommission && <CircularProgress />}
                        {ommission?.summary.frequency && (
                          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: "2rem" }}>
                            {ommission?.summary.frequency.toFixed(1)}
                          </Typography>
                        )}
                      </Card>
                    </Tooltip>

                    <SvgIcon
                      sx={{
                        fontSize: "1rem",
                        width: "15px",
                        height: "30px",
                      }}
                      viewBox="0 0 17 33"
                    >
                      <path d="M1.13245 16.8639H12.1324" stroke="black" stroke-linecap="round" />
                      <path d="M12.1324 1.38538L12.1324 16.6056" stroke="black" stroke-linecap="round" />
                      <path d="M12.1324 31.8258L12.1324 16.6056" stroke="black" stroke-linecap="round" />
                      <path d="M16.4487 1.38538L12.1324 1.38538" stroke="black" stroke-linecap="round" />
                      <path d="M16.4487 31.8258H12.1324" stroke="black" stroke-linecap="round" />
                    </SvgIcon>
                  </Stack>
                </Grid>
                <Grid size={9}>
                  <Stack spacing={1} sx={{ pl: 1 }}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body1" fontWeight="bold">
                        {strings.ommission.totalreports}:
                      </Typography>
                      <Typography variant="body1" sx={{ ml: 2 }}>
                        {ommission?.summary.total}
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body1" fontWeight="bold">
                        {strings.ommission.totalemployees}:
                      </Typography>
                      <Typography variant="body1" sx={{ ml: 2 }}>
                        {ommission?.summary.totalEmployees}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default OmissionComponent;
